
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserProfileData {
  userProfession: string | null;
  rawProfession: string | null;
  displayProfession: string | null;
  userCurrency: string;
  userCountry: string;
  preferredMessaging: string | null;
}

const SERVICE_TYPE_TO_PROFESSION: Record<string, string> = {
  "Beauty & Wellness": "Beauty Professional",
  "Home Services": "Home Service Provider",
  "Professional Services": "Professional Consultant",
  "Health & Fitness": "Fitness Trainer",
  "Education & Tutoring": "Education Specialist",
  "Tech Services": "Technology Expert",
  "Other": "Service Provider"
};

export const useUserProfile = (): [UserProfileData, boolean] => {
  const [profileData, setProfileData] = useState<UserProfileData>({
    userProfession: null,
    rawProfession: null,
    displayProfession: null,
    userCurrency: "KES",
    userCountry: "Kenya",
    preferredMessaging: "whatsapp",
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        setIsLoading(false);
        return;
      }

      if (profile) {
        const rawProfession = profile?.profession || null;
        const displayProfession = profile?.profession || null;
        
        let userCountry = "Kenya";
        if (profile?.country) {
          userCountry = profile.country;
        }
        
        // Get currency from profile settings if available
        let userCurrency = "KES";
        if (profile?.currency) {
          userCurrency = profile.currency;
        } else if (profile?.country === "Ethiopia") {
          userCurrency = "ETB";
        } else if (profile?.country === "Kenya") {
          userCurrency = "KES";
        } else {
          userCurrency = "USD";
        }

        let preferredMessaging = "whatsapp";
        if (profile?.preferred_messaging) {
          preferredMessaging = profile.preferred_messaging;
        }
        
        let suggestionProfession = profile?.profession || null;
        
        if (!suggestionProfession && profile?.service_type) {
          suggestionProfession = SERVICE_TYPE_TO_PROFESSION[profile.service_type] || null;
        }
        
        if (!suggestionProfession) {
          suggestionProfession = "Service Provider";
        }
        
        console.log('Setting user profession for suggestions:', suggestionProfession);
        console.log('Raw profession from profile:', rawProfession);
        console.log('Service type from profile:', profile?.service_type);
        console.log('Country:', profile?.country);
        console.log('Currency:', profile?.currency || userCurrency);
        console.log('Preferred messaging:', preferredMessaging);
        
        setProfileData({
          userProfession: suggestionProfession,
          rawProfession,
          displayProfession,
          userCurrency,
          userCountry,
          preferredMessaging
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
    }
  };

  return [profileData, isLoading];
};
